/**
 * PageRenderer — renders a Page by iterating its parts in sortOrder and
 * delegating to the right part renderer.
 *
 * Passes the page's id + owning scope down to GalleryPart so it can
 * call the public page-scoped items endpoint without needing auth.
 *
 * Used by:
 *   - LandingRouter / ChapterLandingPage (spec 100)
 *   - EventDetailPage (spec 070)
 *   - PageEditor preview pane (spec 080)
 */

import type { PageDetail, PagePart } from '@/services/api';
import { HtmlPart } from './HtmlPart';
import { GalleryPart } from './GalleryPart';

export function PageRenderer({ page }: { page: PageDetail }) {
  const sorted = [...(page.parts ?? [])].sort((a, b) => a.sortOrder - b.sortOrder);
  if (sorted.length === 0) {
    return (
      <div className="container mx-auto px-4 py-16 text-center text-white/60">
        <h1 className="text-3xl font-display font-bold text-white">{page.title}</h1>
        <p className="mt-2">This page has no content yet.</p>
      </div>
    );
  }
  return (
    <article className="page-renderer space-y-6">
      {sorted.map((p) => (
        <RenderPart key={p.id} part={p} pageId={page.id} />
      ))}
    </article>
  );
}

function RenderPart({ part, pageId }: { part: PagePart; pageId: string }) {
  if (part.kind === 'html') {
    return <HtmlPart config={part.config as import('@/services/api').HtmlPartConfig} />;
  }
  if (part.kind === 'gallery') {
    return (
      <GalleryPart
        config={part.config as import('@/services/api').GalleryPartConfig}
        pageId={pageId}
        partId={part.id}
      />
    );
  }
  return null;
}

export default PageRenderer;
